import type { ReactNode } from 'react';
import { Trash2, Bell, Mail, Power, PowerOff } from 'lucide-react';
import type {
  RuleAction,
  NotifyInAppAction,
  NotifyEmailAction,
  RelayOnAction,
  RelayOffAction,
  RecipientSpec,
} from '../../services/sensorRules';
import type { EspNowController, EspNowRouter } from '../../services/espnowIrrigation';
import { usableRelays } from '../../services/espnowIrrigation';
import type { LicensedUser } from '../../types/subscription';

interface Props {
  action: RuleAction;
  onChange: (next: RuleAction) => void;
  onRemove: () => void;
  // Preloaded lookups for the editors — only users holding an irrigation license
  users: LicensedUser[];
  controllers: EspNowController[];
  routers: EspNowRouter[];
}

const ACTION_ICONS: Record<RuleAction['type'], ReactNode> = {
  notify_in_app: <Bell className="w-4 h-4 text-blue-600" />,
  notify_email: <Mail className="w-4 h-4 text-indigo-600" />,
  relay_on: <Power className="w-4 h-4 text-green-600" />,
  relay_off: <PowerOff className="w-4 h-4 text-gray-600" />,
};

const ACTION_LABELS: Record<RuleAction['type'], string> = {
  notify_in_app: 'In-app notification',
  notify_email: 'Email',
  relay_on: 'Turn relay ON',
  relay_off: 'Turn relay OFF',
};

const PLACEHOLDER_HINT = '{value}, {percent}, {liters}, {device_name}, {channel_name}';

export default function RuleActionEditor({
  action, onChange, onRemove, users, controllers, routers,
}: Props) {
  return (
    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
        {ACTION_ICONS[action.type]}
        <span className="font-medium text-sm">{ACTION_LABELS[action.type]}</span>
        <button
          type="button"
          onClick={onRemove}
          className="ml-auto text-gray-400 hover:text-red-600 p-1"
          title="Remove action"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <div className="p-3 space-y-2">
        {(action.type === 'notify_in_app' || action.type === 'notify_email') && (
          <NotifyEditor
            action={action}
            users={users}
            onChange={onChange as (a: NotifyInAppAction | NotifyEmailAction) => void}
          />
        )}
        {action.type === 'relay_on' && (
          <RelayEditor
            action={action}
            controllers={controllers}
            routers={routers}
            onChange={onChange as (a: RelayOnAction) => void}
            showDuration
          />
        )}
        {action.type === 'relay_off' && (
          <RelayEditor
            action={action}
            controllers={controllers}
            routers={routers}
            onChange={onChange as (a: RelayOffAction) => void}
            showDuration={false}
          />
        )}
      </div>
    </div>
  );
}

// ─── Notification editor ──────────────────────────────────────────────────

function NotifyEditor({
  action, users, onChange,
}: {
  action: NotifyInAppAction | NotifyEmailAction;
  users: LicensedUser[];
  onChange: (next: NotifyInAppAction | NotifyEmailAction) => void;
}) {
  const isAllAdmins = action.user_ids === 'all_admins';
  const selectedIds: number[] = Array.isArray(action.user_ids) ? action.user_ids : [];

  function setRecipients(next: RecipientSpec) {
    onChange({ ...action, user_ids: next });
  }

  function toggleUser(id: number) {
    if (selectedIds.includes(id)) {
      setRecipients(selectedIds.filter(u => u !== id));
    } else {
      setRecipients([...selectedIds, id]);
    }
  }

  const isEmail = action.type === 'notify_email';
  const titleField = isEmail ? 'subject' : 'title';
  const titleValue =
    action.type === 'notify_email' ? action.subject || '' : action.title || '';

  return (
    <>
      <div>
        <label className="text-xs text-gray-600">Recipients</label>
        <div className="mt-1 flex items-center gap-3 text-sm">
          <label className="inline-flex items-center gap-1.5">
            <input
              type="radio"
              checked={isAllAdmins}
              onChange={() => setRecipients('all_admins')}
            />
            {/* Still 'all_admins' on the wire (saved rules and shipped bundles
                carry it); the server resolves it to the sensor's farm list. */}
            People on this farm's alert list
          </label>
          <label className="inline-flex items-center gap-1.5">
            <input
              type="radio"
              checked={!isAllAdmins}
              onChange={() => setRecipients([])}
            />
            Specific users
          </label>
        </div>
        {isAllAdmins && (
          <p className="mt-1 text-xs text-gray-500">
            Whoever is on the "Who is notified" list for the sensor's farm, or the irrigation
            admins if nobody is.
          </p>
        )}
        {!isAllAdmins && (
          <div className="mt-2 max-h-32 overflow-y-auto border border-gray-200 rounded p-2 space-y-1">
            {users.length === 0 && (
              <div className="text-xs text-gray-400 italic">No users hold an irrigation licence.</div>
            )}
            {users.map(u => (
              <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(u.id)}
                  onChange={() => toggleUser(u.id)}
                />
                <span>
                  {u.first_name || u.last_name
                    ? `${u.first_name || ''} ${u.last_name || ''}`.trim()
                    : u.username}
                </span>
                <span className="text-xs text-gray-400">{u.email}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <label className="block">
        <span className="text-xs text-gray-600">{isEmail ? 'Subject' : 'Title'}</span>
        <input
          type="text"
          value={titleValue}
          onChange={(e) => onChange({ ...action, [titleField]: e.target.value } as NotifyInAppAction | NotifyEmailAction)}
          placeholder={isEmail ? 'Tank low alert' : 'Tank low'}
          className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
        />
      </label>
      <label className="block">
        <span className="text-xs text-gray-600">Body</span>
        <textarea
          value={action.body || ''}
          onChange={(e) => onChange({ ...action, body: e.target.value })}
          rows={2}
          placeholder="{device_name}: tank at {percent}% ({liters} L)"
          className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm font-mono"
        />
        <span className="text-[11px] text-gray-400 block mt-0.5">
          Placeholders: {PLACEHOLDER_HINT}
        </span>
      </label>
    </>
  );
}

// ─── Relay editor ─────────────────────────────────────────────────────────

function RelayEditor<T extends RelayOnAction | RelayOffAction>({
  action, controllers, routers, onChange, showDuration,
}: {
  action: T;
  controllers: EspNowController[];
  routers: EspNowRouter[];
  onChange: (next: T) => void;
  showDuration: boolean;
}) {
  const availableRouters = routers.filter(r => r.controller_id === action.controller_id);
  const selectedRouter = availableRouters.find(r => r.router_mac === action.router_mac);
  const relayLabels = (selectedRouter?.relay_labels || {}) as Record<string, string>;
  // The relays this node actually has and uses, not the eight the state bitmask
  // can address. A rule pointed at a channel that goes nowhere is a rule that
  // silently never waters anything.
  //
  // Keeps whatever the rule already names, even once that relay is marked out
  // of use — a select showing blank over a rule that still fires is worse than
  // one showing a choice somebody can see and change.
  const relayChoices = usableRelays(selectedRouter);
  if (selectedRouter && !relayChoices.includes(action.relay_index)) {
    relayChoices.push(action.relay_index);
    relayChoices.sort((a, b) => a - b);
  }

  return (
    <>
      <label className="block">
        <span className="text-xs text-gray-600">Controller</span>
        <select
          value={action.controller_id}
          onChange={(e) => onChange({ ...action, controller_id: e.target.value, router_mac: '' })}
          className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
        >
          <option value="">— select —</option>
          {controllers.map(c => (
            <option key={c.controller_id} value={c.controller_id}>
              {c.controller_name || c.controller_id}
            </option>
          ))}
        </select>
      </label>

      <label className="block">
        <span className="text-xs text-gray-600">Router</span>
        <select
          value={action.router_mac}
          onChange={(e) => onChange({ ...action, router_mac: e.target.value })}
          disabled={!action.controller_id}
          className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm disabled:bg-gray-50"
        >
          <option value="">— select —</option>
          {availableRouters.map(r => (
            <option key={r.router_mac} value={r.router_mac}>
              {r.name || r.router_mac}
            </option>
          ))}
        </select>
      </label>

      <label className="block">
        <span className="text-xs text-gray-600">Relay</span>
        <select
          value={action.relay_index}
          onChange={(e) => onChange({ ...action, relay_index: Number(e.target.value) })}
          disabled={!action.router_mac}
          className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm disabled:bg-gray-50"
        >
          {relayChoices.map(i => (
            <option key={i} value={i}>
              Relay {i}{relayLabels[String(i)] ? ` — ${relayLabels[String(i)]}` : ''}
            </option>
          ))}
        </select>
      </label>

      {showDuration && (
        <label className="block">
          <span className="text-xs text-gray-600">Duration (seconds)</span>
          <input
            type="number"
            min={1}
            max={86400}
            value={(action as RelayOnAction).duration_sec ?? 60}
            onChange={(e) => onChange({ ...(action as RelayOnAction), duration_sec: Number(e.target.value) } as T)}
            className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
          />
        </label>
      )}
    </>
  );
}
